import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  background: linear-gradient(45deg, #f0f2f5 25%, transparent 25%),
              linear-gradient(-45deg, #f0f2f5 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #f0f2f5 75%),
              linear-gradient(-45deg, transparent 75%, #f0f2f5 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
`;

const StyledImage = styled.img`
  width: 100%;
  height: ${props => props.$height || 'auto'};
  object-fit: ${props => props.$objectFit || 'cover'};
  transition: opacity 0.3s ease, filter 0.3s ease;
  display: block;

  &.loading {
    opacity: 0.7;
    filter: blur(5px);
  }

  &.loaded {
    opacity: 1;
    filter: none;
  }

  &.error {
    opacity: 0.5;
    background: #f5f5f5;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #666;
  font-size: 0.9rem;
  opacity: ${props => props.$show ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #999;
  font-size: 0.8rem;
  text-align: center;
  opacity: ${props => props.$show ? 1 : 0};
  transition: opacity 0.3s ease;
`;

/**
 * OptimizedImage - Unified image component with modern loading optimizations
 *
 * Features:
 * - JPEG image loading (WebP disabled - server doesn't have WebP files)
 * - Progressive loading with blur effect
 * - Intersection Observer lazy loading
 * - Smart preloading
 * - Consistent error handling
 * - Loading state management
 */
const OptimizedImage = ({
  src,
  alt,
  category = '',
  filename = '',
  className = '',
  height,
  objectFit = 'cover',
  thumbnail = true,
  onLoad,
  onError,
  preload = false,
  ...props
}) => {
  const [loadState, setLoadState] = useState('loading');
  const [imageSrc, setImageSrc] = useState(null);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Build optimized image paths with WebP support
  const buildImagePaths = useCallback(() => {
    if (src) {
      // If src is provided directly, use it
      return {
        webp: src.replace(/\.(jpg|jpeg)$/i, '.webp'),
        fallback: src
      };
    }

    // Build paths from category and filename
    const basePath = '/uploads';
    const categoryPath = category.toLowerCase().replace(/\s+/g, '-');
    const sizePath = thumbnail ? 'thumbs' : 'optimized';

    const jpegPath = `${basePath}/${categoryPath}/${sizePath}/${filename}`;
    const webpPath = jpegPath.replace(/\.(jpg|jpeg)$/i, '.webp');

    return {
      webp: webpPath,
      fallback: jpegPath
    };
  }, [src, category, filename, thumbnail]);

  // Preload image
  const preloadImage = useCallback((imagePath) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(imagePath);
      img.onerror = reject;
      img.src = imagePath;
    });
  }, []);

  // Load image (WebP disabled - server doesn't have WebP files)
  const loadImage = useCallback(async () => {
    if (!src && (!category || !filename)) return;

    const paths = buildImagePaths();
    setLoadState('loading');

    try {
      // Load JPEG directly (skip WebP since server doesn't have WebP files)
      await preloadImage(paths.fallback);
      setImageSrc(paths.fallback);
      setLoadState('loaded');
      onLoad && onLoad();
    } catch (error) {
      console.warn(`Failed to load image: ${paths.fallback}`, error);
      setLoadState('error');
      onError && onError(error);
    }
  }, [src, category, filename, buildImagePaths, preloadImage, onLoad, onError]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (preload) {
      // Load immediately if preload is true
      loadImage();
      return;
    }

    const img = imgRef.current;
    if (!img) return;

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadImage();
            observerRef.current.unobserve(img);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image comes into view
        threshold: 0.1
      }
    );

    observerRef.current.observe(img);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadImage, preload]);

  return (
    <ImageContainer className={className}>
      <StyledImage
        ref={imgRef}
        src={imageSrc || 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>'}
        alt={alt}
        className={loadState}
        $height={height}
        $objectFit={objectFit}
        loading="lazy"
        decoding="async"
        {...props}
      />

      <LoadingOverlay $show={loadState === 'loading'}>
        Loading...
      </LoadingOverlay>

      <ErrorOverlay $show={loadState === 'error'}>
        <div>Image unavailable</div>
        <div style={{ fontSize: '0.7rem', marginTop: '4px' }}>
          {alt || 'Unable to load image'}
        </div>
      </ErrorOverlay>
    </ImageContainer>
  );
};

export default OptimizedImage;